/**
 * `/organization` with no Organisation in its address, where the switcher, the
 * role notice and every "go to Organisation" link arrive.
 *
 * An Organisation's own administrator is sent on to their Organisation's
 * window. An Instance Manager owner or admin opens each Organisation from the
 * ELASO Instance Admin Client, one window each, so they are pointed there
 * rather than offered a list here
 * (docs/adr/39-two-admin-clients-the-elaso-instance-admin-client-and-the-organisation-admin-client.md).
 * Anybody else is told what this account administers: nothing.
 */
import type {
	InstanceAdminSelf,
	OrganizationMe,
} from "@elaso/shared/types/organization";
import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router";
import { Globe } from "lucide-react";
import { useEffect, useState } from "react";
import { ulid } from "ulid";
import { useStore } from "zustand";
import {
	AdminNoAccess,
	type AdminRefusal,
} from "@/components/admin/admin-no-access";
import { AdminLoading, AdminReadFailed } from "@/components/admin/admin-status";
import { ConnectionWaitScreen } from "@/components/auth/connection-wait";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { useConnectionWait } from "@/hooks/use-connection-wait";
import { useWsConnected } from "@/hooks/use-ws-connected";
import { authStore } from "@/lib/stores/auth-store";
import { ws } from "@/lib/websocket-wrapper";

export const Route = createFileRoute("/organization/")({
	component: FindOrganization,
});

type Found = {
	self: OrganizationMe;
	instance: InstanceAdminSelf;
};

/** Why an account that is not sent on administers nothing here. */
const refusalOf = (self: Found["self"]): AdminRefusal => {
	if (!self) return { kind: "no-organization" };
	if ("switchedOff" in self) {
		return { kind: "switched-off", organizationName: self.name };
	}
	return { kind: "not-an-administrator", organizationName: self.name };
};

function FindOrganization() {
	const user = useStore(authStore, (s) => s.user);
	const logout = useStore(authStore, (s) => s.logout);
	const { isConnected, connectionEpoch } = useWsConnected();
	const wait = useConnectionWait(isConnected);
	const [found, setFound] = useState<Found | undefined>();
	const [failed, setFailed] = useState(false);
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		if (!(isConnected && user)) return;
		let current = true;
		setFailed(false);
		Promise.all([
			ws.sendWithPromise<Found["self"]>({
				id: ulid(),
				procedure: "organization.me",
				data: {},
			}),
			ws.sendWithPromise<InstanceAdminSelf>({
				id: ulid(),
				procedure: "instanceAdmin.me",
				data: {},
			}),
		])
			.then(([self, instance]) => {
				if (current) setFound({ self, instance });
			})
			.catch((error) => {
				console.error("Failed to find the caller's organization:", error);
				if (current) setFailed(true);
			});
		return () => {
			current = false;
		};
	}, [isConnected, user, connectionEpoch, attempt]);

	if (!user) return null;

	if (failed && !found) {
		return (
			<AdminReadFailed
				message="Could not read your organization."
				onRetry={() => setAttempt((count) => count + 1)}
			/>
		);
	}

	if (!found) {
		// Nothing is read until the socket opens, so until then the wait is the
		// connection's, and says so.
		if (wait.status !== "connected") {
			return <ConnectionWaitScreen wait={wait} />;
		}
		return <AdminLoading label="Loading organization..." />;
	}

	if (found.self && !("switchedOff" in found.self) && found.self.isAdmin) {
		return (
			<Navigate
				params={{ organizationId: found.self.organizationId }}
				replace
				to="/organization/$organizationId"
			/>
		);
	}

	if (found.instance.admitted) {
		return <OpenFromInstanceAdmin />;
	}

	return (
		<AdminNoAccess
			email={user.email}
			onSignOut={logout}
			refusal={refusalOf(found.self)}
		/>
	);
}

function OpenFromInstanceAdmin() {
	const navigate = useNavigate();

	return (
		<div className="flex h-full items-center justify-center p-4">
			<Card className="w-full max-w-md">
				<CardHeader>
					<CardTitle>Organisations open from Instance admin</CardTitle>
					<CardDescription>
						You manage every organization on this ELASO Instance. Open one from
						the Organisations page in Instance admin, and it opens in a window
						of its own.
					</CardDescription>
				</CardHeader>
				<CardFooter>
					<Button
						className="flex-1"
						onClick={() => navigate({ to: "/instance/organizations" })}
						type="button"
					>
						<Globe className="size-4" />
						Go to Organisations
					</Button>
				</CardFooter>
			</Card>
		</div>
	);
}
